import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Card from "components/card";
import {
  questionnaireService,
  QuestionnaireResultResponse,
  AiFeedbackPayload,
} from "services/questionnaireService";
import { MdRefresh } from "react-icons/md";
import { fetchWithAuth } from "services/api";

// Import all the extracted components
import { LoadingScreen, ErrorScreen } from "./components/StatusScreens";
import ResultHeader from "./components/ResultHeader";
import BasicInfo from "./components/BasicInfo";
import QuestionnaireResponses from "./components/QuestionnaireResponses";
import InterpretationContainer from "./components/InterpretationContainer";
import ScoreDisplay from "./components/ScoreDisplay";

const QuestionnaireResult: React.FC = () => {
  const { detailId } = useParams<{ detailId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<
    QuestionnaireResultResponse["data"] | null
  >(null);
  const [regenerating, setRegenerating] = useState(false);

  useEffect(() => {
    fetchResults();
  }, [detailId]);

  const fetchResults = async () => {
    if (!detailId) {
      setError("ID del cuestionario no encontrado");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      console.log(`Fetching results for questionnaire detail ID: ${detailId}`);

      const response = await questionnaireService.getQuestionnaireResults(
        parseInt(detailId)
      );
      console.log("Response received:", response);

      if (response.success) {
        console.log("Setting result data:", response.data);
        setResult(response.data);
      } else {
        console.error("API returned error:", response.message);
        setError(
          response.message || "El servidor no pudo procesar la solicitud"
        );
      }
    } catch (err: any) {
      console.error("Error fetching questionnaire results:", err);

      // Enhanced error message with details for debugging
      const errorMessage = err.message || "Error desconocido";
      setError(
        `Error al cargar los resultados del cuestionario (ID: ${detailId}): ${errorMessage}`
      );

      // Log additional debugging information
      console.error("Detail ID:", detailId);
      console.error("API Base URL:", window.location.origin);
      console.error("Error occurred at:", new Date().toISOString());
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (result) {
      navigate(`/admin/patients/${result.patient.id}`);
    } else {
      navigate("/admin/patients");
    }
  };

  const handleSubmitFeedback = async (payload: AiFeedbackPayload) => {
    try {
      const response = await questionnaireService.submitAiFeedback(payload);

      if (response.success) {
        // Refresh data after 1 second to show updated feedback
        setTimeout(() => {
          fetchResults();
        }, 1000);
      } else {
        throw new Error(response.message || "Error al enviar el feedback");
      }
    } catch (error: any) {
      console.error("Error submitting feedback:", error);
      throw error;
    }
  };

  const handleRegenerate = async () => {
    if (!detailId) return;

    try {
      setRegenerating(true);
      const response = await fetchWithAuth(
        `/questionnaire-assignments/regenerate`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            detail_id: parseInt(detailId),
          }),
        }
      );

      if (!response.success) {
        throw new Error("Error al regenerar la interpretación");
      }

      // Refresh the results after successful regeneration
      await fetchResults();
    } catch (error) {
      console.error("Error regenerating interpretation:", error);
      setError("Error al regenerar la interpretación");
    } finally {
      setRegenerating(false);
    }
  };

  // Helper function to determine if scores should be displayed based on questionnaire type
  const shouldDisplayScores = (scoringType: string): boolean => {
    // Add questionnaire types that should display scores
    const typesWithScores = [
      "BECK",
      "STAI",
      "OSI",
      "STAXI",
      "JAS",
      "HOLMES_RAHE",
      "IVA",
      "BIENESTAR",
      "CRI",
      "SCL90",
      "BIG_FIVE",
      "TDAH",
      "TDAH2",
      "TDAH3",
    ];
    // Case-insensitive check
    return typesWithScores.some(
      (type) => type.toUpperCase() === scoringType.toUpperCase()
    );
  };

  if (loading) {
    return <LoadingScreen />;
  }

  if (error || !result) {
    return (
      <ErrorScreen
        error={error || "No se pudieron cargar los resultados del cuestionario"}
        onBack={handleBack}
      />
    );
  }

  const { questionnaire, patient, completed_at, responses, interpretation } =
    result;
  const scoringType = questionnaire.scoring_type;

  // Add debug logging to see what's happening
  console.log("DEBUG - Questionnaire:", questionnaire);
  console.log("DEBUG - Scoring Type:", scoringType);
  console.log("DEBUG - Has Interpretation:", !!interpretation);
  console.log("DEBUG - Has Scores:", interpretation && !!interpretation.scores);
  console.log("DEBUG - ShouldDisplayScores:", shouldDisplayScores(scoringType));
  console.log("DEBUG - Types With Scores:", [
    "BECK",
    "STAI",
    "OSI",
    "STAXI",
    "JAS",
    "HOLMES_RAHE",
    "IVA",
    "BIENESTAR",
  ]);

  const hasScores =
    interpretation && interpretation.scores && shouldDisplayScores(scoringType);

  console.log("Questionnaire scoring type:", scoringType);
  console.log("Has scores:", hasScores);

  // Debug logs for OSI questionnaire
  if (scoringType === "OSI" && interpretation && interpretation.scores) {
    console.log("OSI scores structure:", Object.keys(interpretation.scores));
    if (interpretation.scores.ORQ) {
      console.log("ORQ structure:", Object.keys(interpretation.scores.ORQ));
    }
    if (interpretation.scores.PSQ) {
      console.log("PSQ structure:", Object.keys(interpretation.scores.PSQ));
    }
    if (interpretation.scores.PRQ) {
      console.log("PRQ structure:", Object.keys(interpretation.scores.PRQ));
    }
    if (interpretation.interpretations) {
      console.log(
        "interpretations structure:",
        Object.keys(interpretation.interpretations)
      );
    }
  }

  return (
    <div className="min-h-screen bg-gray-100 py-4 md:py-8">
      <div className="container mx-auto max-w-6xl px-4">
        {/* Header */}
        <ResultHeader
          questionnaireName={questionnaire.name}
          patientName={patient.name}
          onBack={handleBack}
        />

        {/* Main container */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* Left column - Basic info and scores */}
          <div className="lg:col-span-1">
            {/* Basic information */}
            <BasicInfo
              patientName={patient.name}
              questionnaireName={questionnaire.name}
              completedAt={completed_at}
              scoringType={scoringType}
              questionnaireDescription={questionnaire.description}
            />

            {/* Regenerate button */}
            <div className="mt-4">
              <button
                onClick={handleRegenerate}
                disabled={regenerating}
                className="flex w-full items-center justify-center rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition-all hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {regenerating ? (
                  <>
                    <div className="border-t-transparent mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white"></div>
                    Regenerando...
                  </>
                ) : (
                  <>
                    <MdRefresh className="mr-2 h-5 w-5" />
                    Regenerar Respuesta
                  </>
                )}
              </button>
            </div>

            {/* Scores section - only show if has scores */}
            {hasScores && (
              <div className="mt-6">
                <Card extra="w-full p-4 md:p-6">
                  <h2 className="mb-4 border-b border-gray-200 pb-2 text-xl font-bold text-navy-700 dark:text-white">
                    Puntuaciones
                  </h2>
                  <ScoreDisplay
                    scores={interpretation.scores}
                    interpretations={interpretation.interpretations || {}}
                    scoringType={scoringType}
                  />
                </Card>
              </div>
            )}
          </div>

          {/* Right column - Responses and Interpretations */}
          <div className="lg:col-span-2">
            {/* Responses */}
            <QuestionnaireResponses
              responses={responses}
              questionnaireType={scoringType}
            />

            {/* Interpretations - show based on interpretation availability */}
            {interpretation && (
              <div className="mt-6">
                <InterpretationContainer
                  interpretation={interpretation}
                  aiLearningExample={result.ai_learning_example}
                  detailId={detailId || ""}
                  onSubmitFeedback={handleSubmitFeedback}
                  questionnaireType={scoringType}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuestionnaireResult;
